<template>
  <RevCard class="px-24 py-12">
    <div class="flex">
      <MinifiedInfo
        class="flex-wrap md:flex-1"
        :has-backpricer-enabled
        :listing
        :publication-state
        :reference-market
        @refresh-listings="$emit('refresh-listings')"
      />

      <button
        class="border-static-default-low flex cursor-pointer items-center justify-center border-l pl-12"
        @click="toggleExpand"
      >
        <IconChevronDown :class="{ 'rotate-180': isExpanded }" />
      </button>
    </div>

    <div
      v-if="isExpanded"
      class="border-static-default-low mt-24 space-y-24 border-t pt-24"
    >
      <MarketList
        :has-backpricer-enabled
        :listing
        :type="MARKET_LIST_TYPES.WITH_BACKBOX"
      />
      <MarketList
        :has-backpricer-enabled
        :listing
        :type="MARKET_LIST_TYPES.WITHOUT_BACKBOX"
      />
      <MarketList
        :has-backpricer-enabled
        :listing
        :type="MARKET_LIST_TYPES.OFFLINE"
      />
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { Listing } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/listings.types'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevCard } from '@ds/components/Card'
import { IconChevronDown } from '@ds/icons/IconChevronDown'

import { useListings } from '~/scopes/listings/pages/index/composables/useListings'

import { MARKET_LIST_TYPES } from './components/MarketList/MarketList.constants'
import MarketList from './components/MarketList/MarketList.vue'
import MinifiedInfo from './components/MinifiedInfo/MinifiedInfo.vue'

const tracking = useTracking()
const { formatListingToTrackingEvent } = useListings()

const props = defineProps<{
  listing: Listing
  publicationState: string
  referenceMarket: MarketCountryCode | ''
  hasBackpricerEnabled: boolean
  shouldExpand?: boolean
}>()

defineEmits(['refresh-listings'])

const isExpanded = ref(props.shouldExpand ?? false)
const toggleExpand = () => {
  isExpanded.value = !isExpanded.value

  if (isExpanded.value) {
    tracking.trackClick({
      page_type: 'listings',
      zone: 'listing_details',
      name: 'open_details',
    })

    tracking.trackBoSellerListingImpression({
      page_type: 'listings',
      zone: 'listing_details',
      name: 'listing_impression',
      ...formatListingToTrackingEvent(props.listing),
    })
  }
}

watch(
  () => props.shouldExpand,
  (newShouldExpand) => {
    isExpanded.value = newShouldExpand
  },
)
</script>
